// === ./src/frontend/components/HowItWorksSection.jsx ===
import React, { useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  alpha,
} from "@mui/material";
import { Search, Wifi, CloudDownload, FolderOpen } from "@mui/icons-material";
import { slideInUp, pulse } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const HowItWorksSection = ({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  const { lang } = useContext(TranslateContext);

  const steps = [
    {
      step: "01",
      icon: <Search sx={{ fontSize: 28 }} />,
      title: lang?.howStep1Title || "Search or Paste",
      color: theme.palette.primary.main,
      desc:
        lang?.howStep1Desc ||
        "Search our built-in torrent index or paste any magnet link directly. We support all major file types — ISOs, archives, documents, and more.",
      mockup: (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Search sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.68rem" }}
            >
              ubuntu-24.04.2-desktop-amd64.iso
            </Typography>
          </Stack>
          {[
            "ubuntu-24.04.2 LTS • 5.2 GB • ▲ 4,821",
            "ubuntu-22.04.5 LTS • 4.7 GB • ▲ 2,103",
          ].map((r, i) => (
            <Box
              key={i}
              sx={{
                p: 0.75,
                borderRadius: 1,
                mb: 0.5,
                bgcolor:
                  i === 0
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                border: `1px solid ${i === 0 ? alpha(theme.palette.primary.main, 0.2) : "transparent"}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="caption"
                color="text.primary"
                sx={{ fontSize: "0.62rem" }}
              >
                {r}
              </Typography>
              <Chip
                label={lang?.howAddBtn || "Add"}
                size="small"
                color="primary"
                sx={{ height: 16, fontSize: "0.55rem", fontWeight: 700 }}
              />
            </Box>
          ))}
        </Box>
      ),
    },
    {
      step: "02",
      icon: <Wifi sx={{ fontSize: 28 }} />,
      title: lang?.howStep2Title || "We Connect to Peers",
      color: theme.palette.warning.main,
      desc:
        lang?.howStep2Desc ||
        "Our high-speed servers connect to the torrent swarm on your behalf. No client needed, no bandwidth used on your device.",
      mockup: (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ fontSize: "0.65rem", display: "block", mb: 1 }}
          >
            {lang?.howPeerNetwork || "PEER NETWORK"}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {Array.from({ length: 18 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  bgcolor:
                    i < 14
                      ? alpha(theme.palette.success.main, 0.7)
                      : alpha(theme.palette.divider, 0.4),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation:
                    i < 14
                      ? `${pulse} ${1 + (i % 3) * 0.3}s ease-in-out infinite`
                      : "none",
                  animationDelay: `${i * 0.1}s`,
                }}
              >
                <Wifi
                  sx={{
                    fontSize: 10,
                    color: i < 14 ? "#fff" : "text.disabled",
                  }}
                />
              </Box>
            ))}
          </Stack>
          <Typography
            variant="caption"
            color="success.main"
            fontWeight={600}
            sx={{ fontSize: "0.62rem", mt: 0.75, display: "block" }}
          >
            14 / 18 {lang?.howPeersActive || "peers active"} • 47.3 MB/s
          </Typography>
        </Box>
      ),
    },
    {
      step: "03",
      icon: <CloudDownload sx={{ fontSize: 28 }} />,
      title: lang?.howStep3Title || "Saved to Your Cloud",
      color: theme.palette.success.main,
      desc:
        lang?.howStep3Desc ||
        "Once complete, files are stored securely in your personal cloud storage. Stream, share, or download them to any device at any time.",
      mockup: (
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.06),
            border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`,
          }}
        >
          {[
            {
              name: "ubuntu-24.04.2-desktop-amd64.iso",
              size: "5.2 GB",
              done: true,
            },
            {
              name: "ubuntu-24.04.2-live-server.iso",
              size: "2.1 GB",
              done: true,
            },
            {
              name: "ubuntu-22.04.5-desktop.iso",
              size: "4.7 GB",
              done: false,
              prog: 68,
            },
          ].map((f, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={1}
              mb={0.75}
            >
              <FolderOpen
                sx={{
                  fontSize: 14,
                  color: f.done ? "success.main" : "warning.main",
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.primary"
                  noWrap
                  sx={{ fontSize: "0.62rem", display: "block" }}
                >
                  {f.name}
                </Typography>
                {!f.done && (
                  <LinearProgress
                    variant="determinate"
                    value={f.prog}
                    sx={{ height: 3, borderRadius: 2, mt: 0.25 }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.6rem", flexShrink: 0 }}
              >
                {f.done ? f.size : `${f.prog}%`}
              </Typography>
            </Stack>
          ))}
        </Box>
      ),
    },
  ];

  return (
    <Box
      id="how-it-works"
      sx={{
        py: { xs: 10, md: 14 },
        background: isDark
          ? alpha(theme.palette.background.paper, 0.4)
          : alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <Container maxWidth="lg">
        <Box textAlign="center" mb={8}>
          <Chip
            label={lang?.howChip || "Simple 3-Step Process"}
            size="small"
            sx={{
              mb: 2,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{
              fontSize: { xs: "2rem", md: "2.8rem" },
              letterSpacing: "-0.03em",
              mb: 2,
            }}
          >
            {lang?.howTitle || "How It Works"}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 520, mx: "auto", lineHeight: 1.7 }}
          >
            {lang?.howSubtitle ||
              "No software to install. No bandwidth limits on your end. Just add a link and let our cloud do the heavy lifting."}
          </Typography>
        </Box>

        {/* ✅ FIX 1: alignItems="stretch" so all Grid items stretch to same height */}
        <Grid container spacing={4} alignItems="stretch">
          {steps.map((s, i) => (
            // ✅ FIX 2: sx={{ display: "flex" }} so the Grid item itself is a flex container
            <Grid size={{ xs: 12, md: 4 }} key={i} sx={{ display: "flex" }}>
              {/* ✅ FIX 3: height: "100%" + display: "flex" + flexDirection: "column" on the animation wrapper */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  animation: `${slideInUp} 0.6s ease both`,
                  animationDelay: `${i * 0.15}s`,
                }}
              >
                <Card
                  sx={{
                    flex: 1, // ← takes all remaining height
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: `1px solid ${alpha(s.color, 0.15)}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: `0 20px 40px ${alpha(s.color, 0.15)}`,
                      borderColor: alpha(s.color, 0.3),
                    },
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${s.color}, ${alpha(s.color, 0.4)})`,
                    },
                  }}
                >
                  {/* ✅ CardContent also flex-column so mockup is always pinned to bottom */}
                  <CardContent
                    sx={{
                      p: 3,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 2.5,
                          color: "#fff",
                          background: `linear-gradient(135deg, ${s.color}, ${alpha(s.color, 0.7)})`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 6px 16px ${alpha(s.color, 0.35)}`,
                        }}
                      >
                        {s.icon}
                      </Box>
                      <Typography
                        variant="h2"
                        fontWeight={900}
                        sx={{
                          fontSize: "3rem",
                          color: alpha(s.color, 0.12),
                          lineHeight: 1,
                          letterSpacing: "-0.04em",
                        }}
                      >
                        {s.step}
                      </Typography>
                    </Stack>
                    <Typography variant="h6" fontWeight={700} mb={1}>
                      {s.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7, mb: 2 }}
                    >
                      {s.desc}
                    </Typography>
                    {/* ✅ mt: "auto" pushes mockup to the bottom of every card */}
                    <Box sx={{ mt: "auto" }}>{s.mockup}</Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
